/**
 * The user's own copy of their dashboard numbers, in localStorage, so every
 * tab paints at once on a revisit and then revalidates in the background.
 *
 *  - One entry per USER id + query key. Every dashboard key already carries the
 *    active store (and period / part / rep), so stores and users never mix.
 *  - An index (`rc-dash:v1:index`) remembers each entry's size and last write;
 *    the total is capped (MAX_TOTAL) and the least recently written entries go
 *    first. One entry may not exceed MAX_ENTRY (a too-big payload is skipped).
 *  - The index records its owner: a different user id wipes everything before
 *    reading or writing (user switch in the same browser). An explicit log out
 *    runs localStorage.clear() (core/utils/storage.clearStorage), which takes
 *    this with it.
 *  - Every storage access is wrapped: private mode, a full quota or blocked
 *    site data must never break a render — the dashboard just loads from the API.
 */

const PREFIX = "rc-dash:v1:";
const INDEX_KEY = `${PREFIX}index`;
export const MAX_TOTAL = 2 * 1024 * 1024; // characters (≈ bytes for this JSON)
export const MAX_ENTRY = 512 * 1024;

const ls = () => {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
};

const readIndex = (store) => {
  try {
    const raw = store.getItem(INDEX_KEY);
    const idx = raw ? JSON.parse(raw) : null;
    if (idx && typeof idx === "object" && idx.entries && typeof idx.entries === "object") return idx;
  } catch {
    // corrupt index — start again
  }
  return { owner: null, entries: {} };
};

const writeIndex = (store, idx) => {
  try {
    store.setItem(INDEX_KEY, JSON.stringify(idx));
  } catch {
    // ignore — the entries are still readable by key
  }
};

const entryKey = (userId, queryKey) => `${PREFIX}${userId}:${JSON.stringify(queryKey)}`;

/** Drop every persisted dashboard entry (all users). */
export const clearDashboardCache = () => {
  const store = ls();
  if (!store) return;
  try {
    const keys = [];
    for (let i = 0; i < store.length; i += 1) {
      const k = store.key(i);
      if (k && k.startsWith(PREFIX)) keys.push(k);
    }
    keys.forEach((k) => store.removeItem(k));
  } catch {
    // nothing to do
  }
};

// A different signed-in user than the one who wrote the cache: wipe it.
const ownedIndex = (store, userId) => {
  const idx = readIndex(store);
  if (idx.owner != null && String(idx.owner) !== String(userId)) {
    clearDashboardCache();
    return { owner: userId, entries: {} };
  }
  idx.owner = userId;
  return idx;
};

/** `{ data, updatedAt, etag }` for this user + key, or undefined. */
export const readDashboardEntry = (userId, queryKey) => {
  if (userId == null) return undefined;
  const store = ls();
  if (!store) return undefined;
  try {
    const idx = readIndex(store);
    if (idx.owner != null && String(idx.owner) !== String(userId)) return undefined;
    const raw = store.getItem(entryKey(userId, queryKey));
    if (!raw) return undefined;
    const e = JSON.parse(raw);
    if (!e || e.data === undefined || typeof e.t !== "number") return undefined;
    return { data: e.data, updatedAt: e.t, etag: e.etag || null };
  } catch {
    return undefined;
  }
};

/** Store `data` for this user + key; evicts the oldest entries past MAX_TOTAL. */
export const writeDashboardEntry = (userId, queryKey, data, { updatedAt = Date.now(), etag = null } = {}) => {
  if (userId == null || data === undefined) return false;
  const store = ls();
  if (!store) return false;
  try {
    const key = entryKey(userId, queryKey);
    const raw = JSON.stringify({ t: updatedAt, etag: etag || undefined, data });
    const idx = ownedIndex(store, userId);
    if (raw.length > MAX_ENTRY) {
      // Too big to keep — and an older copy must not outlive the new numbers.
      store.removeItem(key);
      delete idx.entries[key];
      writeIndex(store, idx);
      return false;
    }
    idx.entries[key] = { t: updatedAt, n: raw.length };
    // LRU by last write: drop the oldest others until everything fits.
    const total = () => Object.values(idx.entries).reduce((s, e) => s + (Number(e?.n) || 0), 0);
    const byAge = Object.entries(idx.entries)
      .filter(([k]) => k !== key)
      .sort((a, b) => (a[1]?.t || 0) - (b[1]?.t || 0));
    while (total() > MAX_TOTAL && byAge.length) {
      const [old] = byAge.shift();
      store.removeItem(old);
      delete idx.entries[old];
    }
    try {
      store.setItem(key, raw);
    } catch {
      // Quota: free everything else of ours once and try again.
      byAge.forEach(([old]) => {
        store.removeItem(old);
        delete idx.entries[old];
      });
      store.setItem(key, raw);
    }
    writeIndex(store, idx);
    return true;
  } catch {
    return false;
  }
};

/** Forget one entry (e.g. it no longer matches what the server has). */
export const removeDashboardEntry = (userId, queryKey) => {
  const store = ls();
  if (!store || userId == null) return;
  try {
    const key = entryKey(userId, queryKey);
    store.removeItem(key);
    const idx = readIndex(store);
    if (idx.entries[key]) {
      delete idx.entries[key];
      writeIndex(store, idx);
    }
  } catch {
    // ignore
  }
};
